import { useEffect, useMemo, useState } from 'react';
import BottomSheet from './BottomSheet';
import NumPad from './NumPad';
import { usePaymentMethods } from '../../../hooks/usePaymentMethods';

const sanitizeDecimal = (raw) => {
  const digits = String(raw || '').replace(/[^0-9.]/g, '');
  const parts = digits.split('.');
  if (parts.length <= 1) return digits;
  return `${parts[0]}.${parts.slice(1).join('')}`;
};

const getMonthValue = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const buildMonthOptions = () =>
  Array.from({ length: 13 }, (_, index) => {
    const date = new Date();
    date.setMonth(date.getMonth() - index);
    const value = getMonthValue(date);
    return {
      value,
      label: date.toLocaleDateString([], { month: 'long', year: 'numeric' }),
    };
  });

const formatPaymentDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const normalizeValue = (value) => String(value || '').trim().toLowerCase();

const paymentsMatchCourse = (payment, course) => {
  if (!course) return false;
  const sameCourse = normalizeValue(payment.courseId) === normalizeValue(course.courseId);
  const paymentBatch = normalizeValue(payment.batchId);
  const courseBatch = normalizeValue(course.batchId);
  return sameCourse && (!paymentBatch || !courseBatch || paymentBatch === courseBatch);
};

const PaymentSheet = ({
  open,
  onClose,
  student,
  activeCourses = [],
  openRequests = [],
  paymentHistory = [],
  onSubmit,
  saving,
  error,
  initialRequestId = '',
}) => {
  const { methods: receptionMethods } = usePaymentMethods('reception');
  const [mode, setMode] = useState('request'); // 'request' | 'adhoc'
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const [courseKey, setCourseKey] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentType, setPaymentType] = useState('monthly');
  const [paymentCycle, setPaymentCycle] = useState(getMonthValue());
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [showAmountPad, setShowAmountPad] = useState(false);
  const monthOptions = useMemo(() => buildMonthOptions(), []);

  useEffect(() => {
    if (!open) return;
    setNotes('');
    setReceiptNumber('');
    setPaymentMethod('cash');
    setPaymentType('monthly');
    setPaymentCycle(getMonthValue());
    setShowAmountPad(false);

    if (openRequests.length) {
      setMode('request');
      setSelectedRequestId(
        initialRequestId &&
          openRequests.some((request) => request.id === initialRequestId)
          ? initialRequestId
          : openRequests[0].id,
      );
      setAmount('');
    } else {
      setMode('adhoc');
      setSelectedRequestId('');
      setAmount('');
      if (activeCourses.length === 1) {
        setCourseKey(
          `${activeCourses[0].courseId}::${activeCourses[0].batchId || ''}`,
        );
      } else {
        setCourseKey('');
      }
    }
  }, [open, openRequests, activeCourses, initialRequestId]);

  const selectedRequest = useMemo(
    () => openRequests.find((request) => request.id === selectedRequestId) || null,
    [openRequests, selectedRequestId],
  );

  const selectedCourse = useMemo(
    () =>
      activeCourses.find(
        (course) => `${course.courseId}::${course.batchId || ''}` === courseKey,
      ) || null,
    [activeCourses, courseKey],
  );

  const selectedCycle = useMemo(
    () => monthOptions.find((option) => option.value === paymentCycle) || null,
    [monthOptions, paymentCycle],
  );

  const paymentMeta = {
    paymentType,
    paymentCycle: paymentType === 'monthly' ? paymentCycle : '',
    paymentCycleLabel:
      paymentType === 'monthly' ? selectedCycle?.label || paymentCycle : '',
  };

  const sortedPaymentHistory = useMemo(
    () =>
      [...paymentHistory].sort(
        (a, b) =>
          new Date(b.paymentDate || b.createdAt || 0) -
          new Date(a.paymentDate || a.createdAt || 0),
      ),
    [paymentHistory],
  );

  const relatedMarkedPayments = useMemo(() => {
    if (!sortedPaymentHistory.length) return [];

    if (mode === 'request' && selectedRequest) {
      return sortedPaymentHistory.filter((payment) => {
        if (payment.requestId && payment.requestId === selectedRequest.id) return true;
        const sameCourse =
          normalizeValue(payment.courseId) === normalizeValue(selectedRequest.courseId);
        const paymentBatch = normalizeValue(payment.batchId);
        const requestBatch = normalizeValue(selectedRequest.batchId);
        const sameBatch = !paymentBatch || !requestBatch || paymentBatch === requestBatch;
        const sameType =
          !selectedRequest.paymentType ||
          normalizeValue(payment.paymentType) === normalizeValue(selectedRequest.paymentType);
        const sameCycle =
          !selectedRequest.paymentCycle ||
          normalizeValue(payment.paymentCycle) === normalizeValue(selectedRequest.paymentCycle);
        return sameCourse && sameBatch && sameType && sameCycle;
      });
    }

    if (mode === 'adhoc' && selectedCourse) {
      return sortedPaymentHistory.filter((payment) => {
        const sameType = normalizeValue(payment.paymentType) === normalizeValue(paymentType);
        const sameCycle =
          paymentType !== 'monthly' ||
          normalizeValue(payment.paymentCycle) === normalizeValue(paymentCycle);
        return paymentsMatchCourse(payment, selectedCourse) && sameType && sameCycle;
      });
    }

    return [];
  }, [
    mode,
    paymentCycle,
    paymentType,
    selectedCourse,
    selectedRequest,
    sortedPaymentHistory,
  ]);

  const visiblePaymentHistory = relatedMarkedPayments.length
    ? relatedMarkedPayments
    : sortedPaymentHistory.slice(0, 3);

  const handleSubmit = () => {
    if (mode === 'request') {
      if (!selectedRequest) return;
      if (paymentMethod !== 'cash' && !receiptNumber) return;
      onSubmit?.({
        mode: 'request',
        paymentRequest: selectedRequest,
        paymentMethod,
        ...paymentMeta,
        receiptNumber,
        notes,
      });
    } else {
      const numericAmount = Number(amount || 0);
      if (!numericAmount || !selectedCourse) return;
      if (paymentMethod !== 'cash' && !receiptNumber) return;
      onSubmit?.({
        mode: 'adhoc',
        course: selectedCourse,
        amount: numericAmount,
        ...paymentMeta,
        paymentMethod,
        receiptNumber,
        notes,
      });
    }
  };

  const submitDisabled =
    saving ||
    (mode === 'request'
      ? !selectedRequest || (paymentMethod !== 'cash' && !receiptNumber)
      : !selectedCourse ||
        !Number(amount || 0) ||
        (paymentMethod !== 'cash' && !receiptNumber));

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Record payment"
      description={student ? `${student.displayName} — ${student.academyStudentId}` : ''}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[52px] rounded-2xl border border-stone-300 bg-white px-5 text-sm font-semibold text-stone-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="min-h-[52px] rounded-2xl bg-[#d61f74] px-5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save payment'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-2 rounded-2xl bg-stone-100 p-1">
          <button
            type="button"
            onClick={() => setMode('request')}
            disabled={!openRequests.length}
            className={`flex-1 rounded-xl py-2 text-sm font-semibold transition ${
              mode === 'request'
                ? 'bg-white text-stone-950 shadow'
                : 'text-stone-500'
            } disabled:opacity-40`}
          >
            Settle due
            {openRequests.length ? ` (${openRequests.length})` : ''}
          </button>
          <button
            type="button"
            onClick={() => setMode('adhoc')}
            disabled={!activeCourses.length}
            className={`flex-1 rounded-xl py-2 text-sm font-semibold transition ${
              mode === 'adhoc'
                ? 'bg-white text-stone-950 shadow'
                : 'text-stone-500'
            } disabled:opacity-40`}
          >
            Custom amount
          </button>
        </div>

        {mode === 'request' ? (
          <div className="space-y-3">
            {!openRequests.length ? (
              <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 px-4 py-5 text-sm text-stone-600">
                No open dues. Switch to custom amount to record an ad-hoc payment.
              </div>
            ) : (
              <div className="grid gap-2">
                {openRequests.map((request) => {
                  const active = selectedRequestId === request.id;
                  return (
                    <button
                      key={request.id}
                      type="button"
                      onClick={() => setSelectedRequestId(request.id)}
                      className={`rounded-2xl border px-4 py-3 text-left transition ${
                        active
                          ? 'border-[#f4b6d2] bg-[#fdf2f8]'
                          : 'border-stone-200 bg-stone-50 hover:border-stone-300'
                      }`}
                    >
                      <p className="text-sm font-semibold text-stone-950">
                        {request.courseName || 'Course'}
                      </p>
                      <p className="text-xs text-stone-600">
                        {request.paymentType || 'Payment'} · due{' '}
                        {request.dueDate || '—'}
                      </p>
                      <p className="mt-1 text-lg font-semibold text-stone-900">
                        LKR {Number(request.amount || 0).toLocaleString()}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {!activeCourses.length ? (
              <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 px-4 py-5 text-sm text-stone-600">
                No active course. Register or assign the student to a course first.
              </div>
            ) : activeCourses.length > 1 ? (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Course
                </p>
                <div className="grid gap-2">
                  {activeCourses.map((course) => {
                    const key = `${course.courseId}::${course.batchId || ''}`;
                    const active = key === courseKey;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setCourseKey(key)}
                        className={`rounded-2xl border px-4 py-3 text-left transition ${
                          active
                            ? 'border-[#f4b6d2] bg-[#fdf2f8]'
                            : 'border-stone-200 bg-stone-50 hover:border-stone-300'
                        }`}
                      >
                        <p className="text-sm font-semibold text-stone-950">
                          {course.courseName}
                        </p>
                        <p className="text-xs text-stone-600">
                          {course.batchName} · {course.branch}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Course
                </p>
                <div className="rounded-2xl border border-[#f4b6d2] bg-[#fdf2f8] px-4 py-3">
                  <p className="text-sm font-semibold text-stone-950">
                    {activeCourses[0].courseName}
                  </p>
                  <p className="text-xs text-stone-600">
                    {activeCourses[0].batchName} Â· {activeCourses[0].branch}
                  </p>
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-stone-700">
                  Payment type
                </span>
                <select
                  value={paymentType}
                  onChange={(event) => setPaymentType(event.target.value)}
                  className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                >
                  <option value="monthly">Monthly fee</option>
                  <option value="registration">Registration fee</option>
                  <option value="other">Other</option>
                </select>
              </label>
              {paymentType === 'monthly' ? (
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-stone-700">
                    Payment cycle
                  </span>
                  <select
                    value={paymentCycle}
                    onChange={(event) => setPaymentCycle(event.target.value)}
                    className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  >
                    {monthOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>

            <div>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-stone-700">
                  Amount (LKR)
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => setAmount(sanitizeDecimal(event.target.value))}
                  onFocus={() => setShowAmountPad(true)}
                  placeholder="0"
                  className="block min-h-[56px] w-full rounded-2xl border border-stone-300 bg-white px-4 text-2xl font-semibold text-stone-950 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </label>
              {showAmountPad ? (
                <div className="mt-3 rounded-2xl border border-stone-200 bg-stone-50 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Number pad
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowAmountPad(false)}
                      className="text-xs font-semibold text-stone-600 hover:text-stone-900"
                    >
                      Hide
                    </button>
                  </div>
                  <NumPad
                    value={amount}
                    onChange={setAmount}
                    allowDecimal
                    maxLength={10}
                  />
                </div>
              ) : null}
            </div>
          </div>
        )}

        {visiblePaymentHistory.length ? (
          <div className="rounded-2xl border border-stone-200 bg-stone-50 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                {relatedMarkedPayments.length ? 'Already marked' : 'Recent payments'}
              </p>
              {relatedMarkedPayments.length ? (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                  {relatedMarkedPayments.length} found
                </span>
              ) : null}
            </div>
            <div className="space-y-2">
              {visiblePaymentHistory.map((payment) => (
                <div
                  key={payment.id || `${payment.paymentDate}-${payment.amount}`}
                  className="rounded-xl border border-stone-200 bg-white px-3 py-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-stone-950">
                        {payment.courseName || 'Course'}
                      </p>
                      <p className="text-xs text-stone-600">
                        {payment.paymentCycleLabel || payment.paymentType || 'Payment'}
                        {payment.paymentDate || payment.createdAt
                          ? ` - ${formatPaymentDate(payment.paymentDate || payment.createdAt)}`
                          : ''}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
                        payment.status === 'completed'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {payment.status === 'completed' ? 'Paid' : 'Marked'}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="font-semibold text-stone-900">
                      LKR {Number(payment.amount || 0).toLocaleString()}
                    </span>
                    {payment.receiptNumber ? (
                      <span className="text-xs text-stone-500">
                        Ref {payment.receiptNumber}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              Method
            </span>
            <select
              value={paymentMethod}
              onChange={(event) => setPaymentMethod(event.target.value)}
              className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            >
              {receptionMethods.length === 0 ? (
                <>
                  <option value="cash">Cash</option>
                  <option value="bank_slip">Slip / reference</option>
                  <option value="card">Card</option>
                </>
              ) : (
                receptionMethods.map((method) => (
                  <option key={method.id} value={method.type || method.id}>
                    {method.label}
                  </option>
                ))
              )}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              Reference
            </span>
            <input
              type="text"
              value={receiptNumber}
              onChange={(event) => setReceiptNumber(event.target.value)}
              placeholder="Receipt no."
              inputMode="text"
              className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </label>
        </div>

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-stone-700">
            Notes (optional)
          </span>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={2}
            className="block w-full rounded-2xl border border-stone-300 px-4 py-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </label>

        {error ? (
          <p className="rounded-2xl bg-red-50 px-4 py-2 text-sm text-red-700">
            {error}
          </p>
        ) : null}
      </div>
    </BottomSheet>
  );
};

export default PaymentSheet;
